import React, { useState, useEffect } from 'react';
import api from '../api';
import { PERMISSIONS, PERMISSION_DESCRIPTIONS } from '../utils/permissions';
import { useTheme } from '../context/ThemeContext';

const ManageUsers = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    permissions: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/users');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', permissions: [] });
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      permissions: user.permissions || []
    });
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete user: ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.delete(`/api/users/${encodeURIComponent(email)}`);
      setSuccess('User deleted successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      
      if (editingUser) {
        await api.put(`/api/users/${encodeURIComponent(editingUser.email)}`, {
          name: formData.name,
          permissions: formData.permissions
        });
        setSuccess('User updated successfully');
      } else {
        await api.post('/api/users', formData);
        setSuccess('User created successfully');
      }

      setShowAddModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  return (
    <div style={{ padding: '30px' }}>
      <div style={{
        backgroundColor: theme.cardBackground,
        borderRadius: '8px',
        boxShadow: `0 2px 4px ${theme.cardShadow}`,
        padding: '30px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            margin: 0,
            color: theme.text
          }}>
            👥 Manage Users
          </h1>
          <button
            onClick={handleAddUser}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ➕ Add User
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.name === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da',
            border: `1px solid ${theme.danger}`,
            borderRadius: '6px',
            color: theme.danger,
            marginBottom: '20px'
          }}>
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.name === 'dark' ? 'rgba(40, 167, 69, 0.2)' : '#d4edda',
            border: `1px solid ${theme.success}`,
            borderRadius: '6px',
            color: theme.success,
            marginBottom: '20px'
          }}>
            ✅ {success}
          </div>
        )}

        {loading && users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>
            Loading users...
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.tableHeader, borderBottom: `2px solid ${theme.border}` }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.text }}>Permissions</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: theme.text }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.email} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '12px', color: theme.text }}>{user.name}</td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px', color: theme.text }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {user.permissions && user.permissions.length > 0 ? (
                          user.permissions.map(perm => (
                            <span
                              key={perm}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: theme.name === 'dark' ? 'rgba(77, 171, 247, 0.2)' : '#e3f2fd',
                                color: theme.primary,
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                            >
                              {perm}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: theme.textMuted, fontSize: '13px', fontStyle: 'italic' }}>No permissions</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ffc107',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '8px',
                          fontSize: '12px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.email)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>
                No users found. Click "Add User" to create one.
              </div>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.cardBackground,
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: `1px solid ${theme.border}`
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: theme.text }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.text }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editingUser}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: editingUser ? theme.tableHeader : theme.inputBackground,
                    color: theme.text
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.text }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: theme.inputBackground,
                    color: theme.text
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: theme.text }}>
                  Permissions
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {Object.entries(PERMISSIONS).map(([key, value]) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: formData.permissions.includes(value) 
                          ? (theme.name === 'dark' ? 'rgba(77, 171, 247, 0.2)' : '#e3f2fd')
                          : theme.cardBackground
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(value)}
                        onChange={() => togglePermission(value)}
                        style={{ marginRight: '10px' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: theme.text }}>{value}</div>
                        <div style={{ fontSize: '12px', color: theme.textMuted }}>
                          {PERMISSION_DESCRIPTIONS[value]}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;

